/**
 * URL → catalogueCode map for the per-role menus.
 *
 * The sidebar hides a switched-off item by matching its `catalogueCode`, but the
 * route guard only has a URL to work with. The catalogue stores ONE route per page
 * (the supervisor/admin one it was seeded from), so a role that reaches the same
 * capability at its own prefix — `/therapist/my-calendar` for the calendar, say —
 * is not covered by the catalogue's own routes and would still be reachable by
 * typing the path. This registry gives CatalogueGuard the missing URL↔code join so
 * a disabled feature is unreachable everywhere, not just gone from the menu.
 *
 * Built from the same annotated menu trees, so the two never drift: adding
 * `catalogueCode` to a menu item is all it takes for the guard to cover its URL.
 * The admin menus (admin.js / adminLevels.js) are intentionally absent — their URLs
 * ARE the catalogue's routes, so the guard already matches them directly.
 */
import supervisor from './supervisor';
import therapist from './therapist';
import inclusionhead from './inclusionhead';
import clinicalhead from './clinicalhead';
import admin from './admin';
import caregiver, { CAREGIVER_CATALOGUE_ROUTES } from './caregiver';
import { ADMIN_CATALOGUE_ROUTES } from './adminLevels';

const collect = (node, out) => {
  if (!node) return out;
  if (Array.isArray(node)) {
    node.forEach((n) => collect(n, out));
    return out;
  }
  if (node.url && node.catalogueCode) out.push({ url: node.url, catalogueCode: node.catalogueCode });
  if (node.children) collect(node.children, out);
  return out;
};

/**
 * Flat [{ url, catalogueCode }] across every menu — the per-role trees plus the
 * admin-persona items. The admin ones come pre-flattened from adminLevels because
 * that menu is generated from a definition rather than a static tree.
 */
export const MENU_CATALOGUE_ROUTES = [
  ...collect([supervisor, therapist, inclusionhead, clinicalhead, admin, caregiver], []),
  ...ADMIN_CATALOGUE_ROUTES,
  // The caregiver's in-context routes, which have no menu item of their own to be
  // collected from — see the note beside CAREGIVER_CATALOGUE_ROUTES.
  ...CAREGIVER_CATALOGUE_ROUTES
];

export default MENU_CATALOGUE_ROUTES;
